import React, { Suspense, useEffect, useState } from 'react';
import "./App.css";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { useSections } from './Shared/Context/SectionContext';
import { ApiHost } from './API';
import Icon from "./assets/Images/logo.png";

// Lazy imports
const OffersControl = React.lazy(() => import('./Dashboard/Page/OffersControl'));
const ServicesControl = React.lazy(() => import('./Dashboard/Page/ServicesControl'));
const GuideControl = React.lazy(() => import('./Dashboard/Page/GuideControl'));
const ReportPage = React.lazy(() => import('./Dashboard/Page/ReportPage'));
const SupportControl = React.lazy(() => import('./Dashboard/Page/SupportControl'));
const InfoSection = React.lazy(() => import('./Shared/Components/Sections/SectionTwo'));
const AdminRole = React.lazy(() => import('./Dashboard/Page/AdminRole'));
const Navbar = React.lazy(() => import("./Shared/Components/Sections/Navbar"));
const UseSection = React.lazy(() => import("./Shared/Components/Sections/UseSection"));
const Services = React.lazy(() => import("./Services/Page/Services"));
const Footer = React.lazy(() => import("./Shared/Components/Sections/Footer"));
const FQSection = React.lazy(() => import("./Shared/Components/Sections/FQSection"));
const Guide = React.lazy(() => import("./Guide/Page/Guide"));
const GuideUsing = React.lazy(() => import("./Guide/Components/GuideUsing"));
const Support = React.lazy(() => import("./Support/Page/Support"));
const Offers = React.lazy(() => import("./Offers/Page/Offers"));
const Login = React.lazy(() => import("./Login/Pages/Login"));
const HomeDashboard = React.lazy(() => import("./Dashboard/Page/HomeDashboard"));
const UsersDashBoard = React.lazy(() => import("./Dashboard/Page/UsersDashBoard"));
const HomeControl = React.lazy(() => import("./Dashboard/Page/HomeControl"));
const AddUserForm = React.lazy(() => import("./Dashboard/Components/AddUserForm"));
const AppPage = React.lazy(() => import('./App/AppPage'));

const AdminChat = React.lazy(() => import("./Dashboard/Page/AdminChat"));
const Redirect = React.lazy(() => import("./Shared/Components/Elements/Redirect"));
const Home = React.lazy(() => import("./Home/Page/Home"));

type Section = {
  id: number;
  title: string;
  page_id: number;
  video_url: string | null;
  image_url: string | null;
  section_name: string;
};

type Feature = {
  id: number;
  name: string;
  image_url: string;
  section_id: number;
  description: string | null;
};

type SectionData = {
  section: Section;
  features: Feature[];
};

const DashboardLayout = ({ children }: any) => (
  <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
);

function App() {
  const { sections } = useSections();
  const [section1, setSection1] = useState<SectionData[] | null>(null);
  const [section2, setSection2] = useState<SectionData | null>(null);
  const [secondLoading, setSecondLoading] = useState(true);

  useEffect(() => {
    if (sections) {
      const data = sections.filter(item => item.section.section_name === "home_section6") || null
      setSection1(data)
      setSection2(sections.find(item => item.section.section_name === "home_section8") || null)
      if (data.length) {
        setTimeout(() => {
          setSecondLoading(false)
        }, 2000);
      }
    }
  }, [sections]);

  const MainLayout = ({ children }: any) => {
    const location = useLocation();
    const isSupportPage = location.pathname.startsWith("/support") || location.pathname.startsWith("/Support")  ; // يتحقق إذا كان المسار هو صفحة الدعم

    return (
      <>
        <Navbar />
        {secondLoading ? <div className="pre-loading"><img src={Icon} alt="" /> </div> : ""}

        <div style={{ overflowX: "hidden" }}>{children}</div>

        {!isSupportPage && section2 && (
          <InfoSection
            imageSrc={`${ApiHost}/api/img/get-image/${section2?.section.image_url || ""}`}
            title="هل توجد أي استفسارات حول خدماتنا؟ "
            linkText="تفقد الدعم الفني"
            linkTo="/support"
            className="question"
            dir={true}
          >
            <p dangerouslySetInnerHTML={{ __html: section2?.section.title || "" }}></p>
          </InfoSection>
        )}

        <FQSection className="FQSection" />
        <Footer />
      </>
    );
  }

  return (
    <Router>
      <Routes>
        {/* 🟢 مسارات لوحة التحكم (بدون Navbar و Footer) */}
        <Route element={<Redirect />}>
          <Route
            path="dashboard"
            element={
              <DashboardLayout>
                <HomeDashboard />
              </DashboardLayout>
            }
          >
            <Route element={<AdminRole />}>
              <Route path="" element={<UsersDashBoard />}>
                <Route path="add" element={<AddUserForm />} />
              </Route>
              <Route path="support" element={<SupportControl />} />
              <Route path="home" element={<HomeControl />} />
              <Route path="offers" element={<OffersControl />} />
              <Route path="services" element={<ServicesControl />} />
              <Route path="guide" element={<GuideControl />} />
            </Route>

            <Route path="report" element={<ReportPage />} />
          </Route>
        </Route>

        {/* 🟢 مسارات الصفحات العامة (مع Navbar و Footer) */}
        <Route
          path="/"
          element={
            <MainLayout>
              <Home />
            </MainLayout>
          }
        >
          <Route index element={<UseSection data={section1 || null} />} />
          <Route path=":id" element={<UseSection data={section1 || null} />} />
        </Route>
        <Route
          path="home"
          element={
            <MainLayout>
              <Home />
            </MainLayout>
          }
        >
          <Route index element={<UseSection data={section1} />} />
          <Route path=":id" element={<UseSection data={section1} />} />
        </Route>

        <Route
          path="offers"
          element={
            <MainLayout>
              <Offers />
            </MainLayout>
          }
        />

        <Route
          path="services"
          element={
            <MainLayout>
              <Services />
            </MainLayout>
          }
        />

        <Route
          path="guide"
          element={
            <MainLayout>
              <Guide />
            </MainLayout>
          }
        >
          <Route index element={<GuideUsing data={section1 || null} />} />
          <Route path=":id" element={<GuideUsing data={section1 || null} />} />
        </Route>

        <Route
          path="support"
          element={
            <MainLayout>
              <Support />
            </MainLayout>
          }
        />

        <Route
          path="login"
          element={
            <MainLayout>
              <Login />
            </MainLayout>
          }
        />

        <Route path="app" element={<AppPage />} />
      </Routes>

    </Router>
  );
}

export default App;
